import React, { useMemo, useState } from 'react';
import { Input, Button, Pagination, Row, Col, Modal, Space, Typography, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { SERVER_IP } from 'assets/Config';
import AccessSelectionPanel from 'components/access-selection-panel';
import TableComponent from 'components/table-component';
import { deleteApi } from 'redux/sagas/deleteApiSaga';
import AddGym from 'pages/gyms/add-gym';
import PayNowModal from 'pages/gyms/pay-now';
import RenewModal from 'pages/gyms/renew';
import { getRowColor } from 'helpers';
import { useI18n } from 'hooks/useI18n';

const GymListPresentational = ({
  filteredData,
  column,
  tableLoading,
  rowSelection,
  selectedRowKeys,
  handleAddGym,
  currentPage,
  pageSize,
  initialPageSizeOptions,
  initialPageSize,
  handleTableChange,
  setSearchKey,
  getStartingValue,
  getEndingValue,
  gymAddModal,
  menuSettingsModalOpen,
  menuSettingsGym,
  enabledMenusDraft,
  setEnabledMenusDraft,
  handleSaveMenuSettings,
  configurableMenuOptions,
  defaultEnabledMenus,
  updatingMenuAccess,
  payNowAddModal,
  setPayNowAddModal,
  setGymAddModal,
  refreshList,
  editGym,
  handleClose,
  changingStatus,
  renewGymModal,
  renewGym,
  setRenewGymModal,
  setRenewGym,
}) => {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const [deleteModal, setDeleteModal] = useState(false);

  const enabledCount = enabledMenusDraft.length;
  const allSelected = enabledMenusDraft.length === defaultEnabledMenus.length;
  const optionLookup = useMemo(
    () =>
      configurableMenuOptions.reduce((lookup, option) => {
        lookup[option.value] = option.label;
        return lookup;
      }, {}),
    [configurableMenuOptions]
  );

  const onDeleteGym = () => {
    const url = `${SERVER_IP}api/users/${selectedRowKeys?.[0]}`;
    dispatch(deleteApi('DELETE_GYM', url));
    setDeleteModal(false);
  };

  const handleSearchChange = (e) => {
    setSearchKey(e.target.value);
  };

  const renderSearchAndActions = () => (
    <Row align="middle" justify="space-between" style={{ gap: 8 }}>
      <Col flex="auto">
        <Space size={8}>
          <Input.Search
            placeholder={t('common.search')}
            allowClear
            style={{ width: 240 }}
            onChange={handleSearchChange}
          />
          {selectedRowKeys?.length === 1 && (
            <Popconfirm
              title={t('gyms.delete_confirm')}
              okText={t('common.delete')}
              cancelText={t('common.no')}
              okType="danger"
              onConfirm={() => setDeleteModal(true)}
            >
              <Button type="text" danger size="small" icon={<DeleteOutlined />}>
                {t('common.delete')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Col>
      <Col>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddGym}>
          {t('gyms.create_gym')}
        </Button>
      </Col>
    </Row>
  );

  const renderPaginationFooter = () => (
    <Row align="middle" justify="space-between">
      <Col>
        {!!filteredData.length && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {t('gyms.footer.showing_range', {
              start: getStartingValue(),
              end: getEndingValue(),
              total: filteredData.length,
            })}
          </Typography.Text>
        )}
      </Col>
      <Col>
        <Pagination
          pageSizeOptions={initialPageSizeOptions}
          defaultPageSize={initialPageSize}
          showSizeChanger={true}
          total={filteredData.length}
          onChange={handleTableChange}
          responsive
        />
      </Col>
    </Row>
  );

  const renderDeleteModal = () => (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 18 }} />
          <span>{t('common.delete')}</span>
        </Space>
      }
      open={deleteModal}
      onOk={onDeleteGym}
      onCancel={() => setDeleteModal(false)}
      okText={t('common.confirm')}
      okType="danger"
      cancelText={t('common.cancel')}
    >
      <p style={{ marginTop: 8 }}>{t('gyms.delete_warning')}</p>
    </Modal>
  );

  const renderMenuSettingsModal = () => (
    <Modal
      title={t('gyms.menu_settings.title')}
      open={menuSettingsModalOpen}
      onOk={handleSaveMenuSettings}
      onCancel={handleClose}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={updatingMenuAccess}
      width={840}
      className="gym-menu-settings-modal"
    >
      <AccessSelectionPanel
        description={t('gyms.menu_settings.description', { gymName: menuSettingsGym?.gymName || '-' })}
        helpText={t('gyms.menu_settings.help_text')}
        summaryLabel={t('gyms.menu_settings.enabled_summary')}
        summaryValue={enabledCount}
        selectAllLabel={t('gyms.menu_settings.select_all')}
        clearAllLabel={t('gyms.menu_settings.clear_all')}
        onSelectAll={() => setEnabledMenusDraft(defaultEnabledMenus)}
        onClearAll={() => setEnabledMenusDraft([])}
        allSelected={allSelected}
        enabledLabel={t('common.enabled')}
        disabledLabel={t('common.disabled')}
        options={configurableMenuOptions}
        selectedValues={enabledMenusDraft}
        onToggle={(value) =>
          setEnabledMenusDraft((currentValue) =>
            currentValue.includes(value)
              ? currentValue.filter((currentItem) => currentItem !== value)
              : [...currentValue, value]
          )
        }
        getOptionDescription={(option) => t('gyms.menu_settings.feature_card_copy', { feature: optionLookup[option.value] })}
      />
    </Modal>
  );

  return (
    <>
      <Row>
        <Col span={24} style={{ padding: 20 }}>
          <TableComponent
            loading={tableLoading || changingStatus}
            className="custom-table gym_list_page"
            style={{ width: '100%' }}
            columns={column}
            bordered
            rowKey={(record) => record.id}
            dataSource={filteredData}
            rowSelection={rowSelection}
			rowClassName={(record) => {
				return getRowColor(record?.nextBillDate);
			}}
            title={renderSearchAndActions}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              position: ['none', 'none'],
            }}
            footer={renderPaginationFooter}
          />
        </Col>
      </Row>
      
      {renderDeleteModal()}
      {renderMenuSettingsModal()}

      <AddGym {...{ gymAddModal, setGymAddModal, refreshList, editGym, handleClose }} />
      <PayNowModal {...{ payNowAddModal, setPayNowAddModal, refreshList, editGym, handleClose }} />
      <RenewModal {...{ renewGymModal, setRenewGymModal, renewGym, refreshList, handleClose, setRenewGym }} />
    </>
  );
};

export default GymListPresentational;
